// API URL設定を直接埋め込み（Vercel対応）
const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};
const API_URL = getApiUrl();
import { getImageUrl } from '../utils/imageUrlHelper.js';

/**
 * アクター種別に応じたアバター表示コンポーネント
 *
 * @param {Object} props
 * @param {string} props.src - 画像URL
 * @param {string} props.alt - alt属性
 * @param {string} props.actorType - アクター種別 ('user' | 'store' | 'dao')
 * @param {string} props.actorStatus - アクターの状態 ('active' | 'away_1h' | 'away_12h' | 'inactive')
 * @param {string} props.size - サイズ ('sm' | 'md' | 'lg' | 'xl')
 * @param {string} props.className - 追加のCSSクラス
 * @param {Function} props.onClick - クリックハンドラー
 */
const ActorAvatar = ({
  actor,
  src,
  alt = '',
  actorType = 'user',
  actorStatus = 'active',
  size = 'md',
  className = '',
  onClick,
  onLoad,
  onError,
  actorId: _actorId,
  displayName: _displayName,
  profileImageUrl,
  showName: _showName,
  noPadding = false,
  ..._domProps
}) => {
  if (actor) {
    src = src || actor.profile_image_url;
    alt = alt || actor.display_name || '';
    actorType = actorType === 'user' ? (actor.actor_type || actor.actorType || actor.type || 'user') : actorType;
    actorStatus = actor.actorStatus || actorStatus;
    profileImageUrl = profileImageUrl || actor.profile_image_url;
  }
  // サイズクラスマッピング（アスペクト比1:1に統一）
  const sizeClasses = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-32 h-32',
  };

  // サイズ値マッピング（CSS変数用）
  const sizeValues = {
    xs: '1.5rem',
    sm: '2rem',
    md: '3rem',
    lg: '4rem',
    xl: '8rem',
  };

  // アクター種別による基本縁どりスタイル
  const getActorBorderStyle = (type) => {
    switch (type) {
      case 'dao':
        // 木の年輪風・円形グラデーション
        return {
          borderRadius: '50%',
          background: `
            radial-gradient(circle at center, 
              transparent 85%, 
              rgba(237, 137, 54, 0.3) 87%, 
              transparent 89%,
              rgba(237, 137, 54, 0.5) 91%,
              transparent 93%,
              rgba(237, 137, 54, 0.7) 95%,
              transparent 97%,
              rgba(237, 137, 54, 0.9) 99%
            )
          `,
          padding: '4px',
        };

      case 'store':
        // 角丸の六角形風
        return {
          clipPath:
            'polygon(25% 0%, 75% 0%, 100% 25%, 100% 75%, 75% 100%, 25% 100%, 0% 75%, 0% 25%)',
          background:
            'linear-gradient(45deg, rgba(72, 187, 120, 0.2), rgba(72, 187, 120, 0.8))',
          padding: '3px',
        };

      case 'user':
      default:
        // 柔らかい丸縁
        return {
          borderRadius: '50%',
          background: 'rgba(66, 153, 225, 0.1)',
          border: '2px solid rgba(66, 153, 225, 0.4)',
          padding: '2px',
        };
    }
  };

  // 状態による縁どりエフェクト（activeの場合のみ適用）
  const getStatusEffect = (status) => {
    switch (status) {
      case 'active':
        return {
          animation: 'gentle-pulse 3s ease-in-out infinite',
          boxShadow: '0 0 15px rgba(66, 153, 225, 0.3)',
        };

      default:
        return {};
    }
  };

  const borderStyle = noPadding ? {} : getActorBorderStyle(actorType);
  const statusStyle = getStatusEffect(actorStatus);
  const sizeClass = sizeClasses[size] || sizeClasses.md;
  const sizeValue = sizeValues[size] || sizeValues.md;

  // 相対パスを絶対パスに変換
  const getAbsoluteSrc = (srcValue) => {
    if (!srcValue) return null;
    if (srcValue.startsWith('blob:')) return srcValue;
    return getImageUrl(srcValue, API_URL);
  };

  const absoluteSrc = getAbsoluteSrc(src || profileImageUrl);

  // 世界観との接続エフェクト用のクラス名を生成
  const getWorldViewClass = (type) => {
    switch (type) {
      case 'dao':
        return 'actor-avatar-dao';
      case 'store':
        return 'actor-avatar-store';
      case 'user':
        return 'actor-avatar-user';
      default:
        return 'actor-avatar-user';
    }
  };

  return (
    <>
      {/* CSS アニメーション定義 */}
      <style>
        {`
          @keyframes gentle-pulse {
            0%, 100% { 
              box-shadow: 0 0 15px rgba(66, 153, 225, 0.3);
              transform: scale(1);
            }
            50% { 
              box-shadow: 0 0 25px rgba(66, 153, 225, 0.5);
              transform: scale(1.02);
            }
          }

          @keyframes forest-connection {
            0%, 100% { 
              filter: brightness(1);
            }
            50% { 
              filter: brightness(1.1);
            }
          }

          .actor-avatar-dao {
            animation: forest-connection 4s ease-in-out infinite;
          }

          .actor-avatar-store {
            transition: all 0.3s ease;
          }

          .actor-avatar-store:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
          }

          .actor-avatar-user {
            transition: all 0.2s ease;
          }

          .actor-avatar-user:hover {
            transform: scale(1.05);
          }
        `}
      </style>

      <div
        className={`
          ${getWorldViewClass(actorType)} 
          ${className} 
          ${onClick ? 'cursor-pointer' : ''}
          relative inline-block
        `}
        style={{
          ...borderStyle,
          ...statusStyle,
        }}
        onClick={onClick}
      >
        <img
          src={
            absoluteSrc ||
            `https://api.dicebear.com/7.x/initials/svg?seed=${alt}`
          }
          alt={alt}
          className={`${sizeClass} object-cover flex-shrink-0`}
          style={{
            borderRadius: actorType === 'store' ? '8px' : '50%',
            aspectRatio: '1 / 1',
            objectFit: 'cover',
            objectPosition: 'center',
            width: sizeValue,
            height: sizeValue,
          }}
          onLoad={onLoad}
          onError={(e) => {
            if (onError) onError(e);
            // 画像の読み込みに失敗した場合はdicebearフォールバックに切り替え
            e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${alt}`;
          }}
        />

        {/* 状態インジケーター */}
        {actorStatus === 'active' && (
          <div
            className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"
            title="オンライン"
          />
        )}

        {actorStatus === 'away_1h' && (
          <div
            className="absolute -bottom-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border-2 border-white"
            title="退席中（1時間以内）"
          />
        )}

        {actorStatus === 'away_12h' && (
          <div
            className="absolute -bottom-1 -right-1 w-3 h-3 bg-orange-400 rounded-full border-2 border-white"
            title="退席中（12時間以上）"
          />
        )}

        {actorStatus === 'inactive' && (
          <div
            className="absolute -bottom-1 -right-1 w-3 h-3 bg-gray-400 rounded-full border-2 border-white"
            title="オフライン"
          />
        )}
      </div>
    </>
  );
};

export default ActorAvatar;
